import { type VariantProps, cva } from "class-variance-authority";
import type * as React from "react";

import { cn } from "../lib/utils";

const alertVariants = cva(
	"relative w-full rounded-lg border p-4 [&>svg]:absolute [&>svg]:text-foreground [&>svg]:left-4 [&>svg]:top-4 [&>svg+div]:translate-y-[-3px] [&:has(svg)]:pl-11",
	{
		variants: {
			variant: {
				default: "bg-background text-foreground",
				destructive:
					"text-destructive border-destructive/50 dark:border-destructive [&>svg]:text-destructive text-destructive",
			},
		},
		defaultVariants: {
			variant: "default",
		},
	},
);

const Alert = <V extends VariantProps<typeof alertVariants>>({
	ref,
	className,
	variant,
	...props
}: React.HTMLAttributes<HTMLDivElement> & {
	ref?: React.RefObject<HTMLDivElement>;
	variant?: V["variant"];
}) => (
	<div
		ref={ref}
		role="alert"
		className={cn(alertVariants({ variant }), className)}
		{...props}
	/>
);
Alert.displayName = "Alert";

const AlertTitle = ({
	ref,
	className,
	...props
}: React.HTMLAttributes<HTMLHeadingElement> & {
	ref?: React.RefObject<HTMLParagraphElement>;
}) => (
	<h5
		ref={ref}
		className={cn("mb-1 font-medium leading-none tracking-tight", className)}
		{...props}
	/>
);
AlertTitle.displayName = "AlertTitle";

const AlertDescription = ({
	ref,
	className,
	...props
}: React.HTMLAttributes<HTMLParagraphElement> & {
	ref?: React.RefObject<HTMLParagraphElement>;
}) => (
	<div
		ref={ref}
		className={cn("text-sm [&_p]:leading-relaxed", className)}
		{...props}
	/>
);
AlertDescription.displayName = "AlertDescription";

export { Alert, AlertTitle, AlertDescription };
